import { useState } from "react";
import { App, Button, Form, Input, Popconfirm, Select, Space, Tooltip, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Brand } from "@glow-price/shared";
import { ENTITY_STATUSES } from "@glow-price/shared";

import { brandsApi } from "../api/resources";
import { AliasesInput } from "../components/AliasesInput";
import { CrudDrawer } from "../components/CrudDrawer";
import { ImageUpload } from "../components/ImageUpload";
import { EmptyState, EnhancedTable, PageHeader } from "../components";

const STATUS_OPTIONS = Object.values(ENTITY_STATUSES).map((v) => ({ value: v, label: v }));

export function BrandsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Brand | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["brands", q, page],
    queryFn: () => brandsApi.list({ q, page, pageSize: 20 }),
  });

  const saveMutation = useMutation({
    mutationFn: async (values: Partial<Brand>) => {
      if (editing) return brandsApi.update(editing.id, values);
      return brandsApi.create(values);
    },
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["brands"] });
      setDrawerOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => brandsApi.remove(id),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["brands"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const items = data?.items ?? [];
  const showEmpty = !isLoading && items.length === 0;

  return (
    <div>
      <PageHeader
        title="品牌管理"
        description="维护品牌中英文名、Logo 与别名，供标准商品与渠道商品关联。"
        stats={[{ label: `共 ${data?.total ?? 0} 个品牌` }]}
        extra={
          <Space>
            <Input.Search
              placeholder="搜索中/英文名"
              allowClear
              onSearch={(v) => {
                setPage(1);
                setQ(v);
              }}
              style={{ width: 260 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setDrawerOpen(true);
              }}
            >
              新建品牌
            </Button>
          </Space>
        }
      />
      {showEmpty ? (
        <EmptyState
          title="暂无品牌"
          description="尚未创建任何品牌。点击「新建品牌」开始添加。"
          action={
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setDrawerOpen(true);
              }}
            >
              新建品牌
            </Button>
          }
        />
      ) : (
        <EnhancedTable<Brand>
          rowKey="id"
          loading={isLoading}
          dataSource={items}
          persistKey="brands"
          exportFilename="brands"
          pagination={{
            current: page,
            pageSize: 20,
            total: data?.total ?? 0,
            onChange: (p) => setPage(p),
            showSizeChanger: false,
          }}
          columns={[
            {
              title: "Logo",
              dataIndex: "logoUrl",
              width: 72,
              render: (v: string | null, row) =>
                v ? (
                  <img
                    src={v}
                    alt={row.nameCn}
                    style={{
                      width: 40,
                      height: 40,
                      objectFit: "contain",
                      borderRadius: 6,
                      background: "#FAF6F0",
                      padding: 4,
                    }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                    }}
                  />
                ) : (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    —
                  </Typography.Text>
                ),
            },
            { title: "中文名", dataIndex: "nameCn" },
            { title: "英文名", dataIndex: "nameEn" },
            {
              title: "别名",
              dataIndex: "aliases",
              render: (v: string[]) => v.join(" / "),
            },
            { title: "状态", dataIndex: "status", width: 100 },
            {
              title: "操作",
              key: "actions",
              width: 110,
              render: (_, row) => (
                <Space size={4}>
                  <Tooltip title="编辑">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditing(row);
                        setDrawerOpen(true);
                      }}
                    />
                  </Tooltip>
                  <Popconfirm title="确认删除？" onConfirm={() => deleteMutation.mutate(row.id)}>
                    <Tooltip title="删除">
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      )}
      <CrudDrawer<Partial<Brand>>
        open={drawerOpen}
        title={editing ? "编辑品牌" : "新建品牌"}
        initialValues={editing ?? { aliases: [], status: ENTITY_STATUSES.ACTIVE }}
        loading={saveMutation.isPending}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        onSubmit={(values) => saveMutation.mutateAsync(values)}
      >
        {() => (
          <>
            <Form.Item name="nameCn" label="中文名" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="nameEn" label="英文名">
              <Input />
            </Form.Item>
            <Form.Item name="logoUrl" label="Logo">
              <ImageUpload folder="brand-logos" />
            </Form.Item>
            <Form.Item name="aliases" label="别名">
              <AliasesInput />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </>
        )}
      </CrudDrawer>
    </div>
  );
}
